import { Injectable, inject } from '@angular/core';
import { DiscountApplicationResultDTO } from '@libs/dtos/discount/discount.dto';
import { DiscountService } from '@services/discount/discount.service';
import { ProductService } from '@services/products/product.service';

/** Discount the backend will apply to a total (automatic one, or a promo code) */
export interface DiscountPreview {
	discountName: string | null;
	originalPrice: number;
	discountAmount: number;
	finalPrice: number;
	discountType: string | null;
	discountValue: number | null;
}

/** Outcome of trying a promo code on the current price */
export type PromoResult = 'applied' | 'invalid' | 'error';

function toPreview(result: DiscountApplicationResultDTO): DiscountPreview | null {
	return result.discountAmount > 0
		? {
				discountName: result.discountName,
				originalPrice: result.originalPrice,
				discountAmount: result.discountAmount,
				finalPrice: result.finalPrice,
				discountType: result.discountType,
				discountValue: result.discountValue,
			}
		: null;
}

/**
 * Price of an order form: POST /products/calculate-price for product + quantity, then the discount
 * preview (GET /discounts/preview) for a total. Display only: the backend recomputes both when the
 * order is created. Provided by the page (one per form).
 *
 * Only the latest request counts: every new price request (or clear()) makes the answers still in
 * flight stale, so a slow answer for an older quantity never overwrites the current one.
 */
@Injectable()
export class PricingService {
	private readonly productService = inject(ProductService);
	private readonly discountService = inject(DiscountService);

	/** Price from the API for the last product + quantity (0 = none yet, or it failed) */
	price = 0;
	/** Volume discount % already included in `price` */
	discountPercent = 0;
	/** The last price request failed: no price is shown (never a made-up one) and a retry is offered */
	priceError = false;
	/** Discount preview for the current total (null = none) */
	discount: DiscountPreview | null = null;

	/** Promo code typed by the user and accepted by the preview */
	promoCode = '';
	promoCodeApplied = false;

	private requestId = 0;
	private previewRequestId = 0;
	/** product:quantity of the current `price` (only used by quote(..., { reuse: true })) */
	private pricedKey: string | null = null;

	/** Crossed-out price before the volume discount (display only: the API gives the %, not the base) */
	get volumeOriginalPrice(): number {
		return this.discountPercent > 0 ? Math.round((this.price / (1 - this.discountPercent / 100)) * 100) / 100 : 0;
	}

	/** No price: answers in flight are dropped (priceError is kept until the next request) */
	clear(): void {
		this.requestId++;
		this.pricedKey = null;
		this.price = 0;
		this.discountPercent = 0;
		this.discount = null;
	}

	/** Forget the promo code (the discount preview is left as it is) */
	clearPromo(): void {
		this.promoCode = '';
		this.promoCodeApplied = false;
	}

	/**
	 * Asks the price of `quantity` units of the product. `onPriced` runs with it when it arrives (and is
	 * still the latest request); `onError` after a failure has cleared the price.
	 * With `reuse`, the same product + quantity as the current price answers at once without a request.
	 */
	quote(
		productId: number,
		quantity: number,
		onPriced: (price: number) => void,
		options: { reuse?: boolean; onError?: () => void } = {},
	): void {
		const key = `${productId}:${quantity}`;
		if (options.reuse && key === this.pricedKey) {
			this.requestId++; // a request still in flight is for another quantity: no longer valid
			onPriced(this.price);
			return;
		}

		const requestId = ++this.requestId;
		this.priceError = false;
		this.productService.calculatePrice(productId, quantity).subscribe({
			next: (response) => {
				if (requestId !== this.requestId) return;
				this.pricedKey = key;
				this.price = response.price;
				this.discountPercent = response.discountPercent;
				onPriced(response.price);
			},
			error: () => {
				if (requestId !== this.requestId) return;
				this.pricedKey = null;
				this.price = 0;
				this.discountPercent = 0;
				this.discount = null;
				this.priceError = true;
				options.onError?.();
			},
		});
	}

	/**
	 * Discount the backend will apply to `basePrice` (with the applied promo code, if any).
	 * No request for a base of 0: no discount. The answer is dropped if a price request (or clear())
	 * or another preview came after it; a preview never invalidates a price request in flight.
	 */
	previewDiscount(basePrice: number): void {
		if (basePrice <= 0) {
			this.discount = null;
			return;
		}
		const priceId = this.requestId;
		const previewId = ++this.previewRequestId;
		const stale = () => priceId !== this.requestId || previewId !== this.previewRequestId;
		this.discountService.previewDiscount(basePrice, this.promoCode || undefined).subscribe({
			next: (result) => {
				if (stale()) return;
				this.discount = toPreview(result);
			},
			error: () => {
				if (stale()) return;
				this.discount = null;
			},
		});
	}

	/**
	 * Tries a promo code on the current price. Applied: it is kept and its discount shown.
	 * Invalid or failed: the code is dropped (on 'invalid' the discount shown is cleared too).
	 */
	applyPromoCode(code: string, onResult: (result: PromoResult) => void): void {
		if (!code || this.price <= 0) return;
		this.promoCode = code;
		this.discountService.previewDiscount(this.price, code).subscribe({
			next: (result) => {
				if (result.discountAmount > 0) {
					this.promoCodeApplied = true;
					this.discount = toPreview(result);
					onResult('applied');
				} else {
					this.clearPromo();
					this.discount = null;
					onResult('invalid');
				}
			},
			error: () => {
				this.clearPromo();
				onResult('error');
			},
		});
	}

	/** Drops the promo code and previews the automatic discount alone */
	removePromoCode(): void {
		this.clearPromo();
		this.previewDiscount(this.price);
	}
}
